import { useState } from "react";
import { Eye, FileImage, FileText, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { FilePreviewDialog } from "@/components/files/FilePreviewDialog";
import type { FileRow } from "@/components/files/files-types";

type Props = {
  fileId: string;
  fileName: string;
  onRemove?: () => void;
};

const EXTENSOES_DE_IMAGEM = ["PNG", "JPG", "JPEG", "WEBP"];

/** Só o que dá pra saber sem baixar o arquivo: a extensão do nome. O tipo real vive no servidor. */
function tipoDoArquivo(fileName: string) {
  const partes = fileName.split(".");
  const extensao = partes.length > 1 ? (partes.pop() ?? "").toUpperCase().slice(0, 4) : "";
  return { extensao, ehImagem: EXTENSOES_DE_IMAGEM.includes(extensao) };
}

/** Chip de arquivo já anexado — baixa sob demanda via URL assinada de curta duração. Mostra nome e
 *  tipo: sem o tipo não dava pra conferir se subiu o arquivo certo, só o nome truncado. */
export function FileAttachment({ fileId, fileName, onRemove }: Props) {
  const utils = trpc.useUtils();
  const { extensao, ehImagem } = tipoDoArquivo(fileName);
  const Icone = ehImagem ? FileImage : FileText;
  const [preview, setPreview] = useState<FileRow | null>(null);
  const [carregandoPreview, setCarregandoPreview] = useState(false);

  async function handleDownload() {
    try {
      const { url } = await utils.file.getDownloadUrl.fetch({ fileId });
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      toast.error("Não foi possível baixar o arquivo.");
    }
  }

  async function handlePreview() {
    setCarregandoPreview(true);
    try {
      const file = await utils.client.file.getById.query({ fileId });
      setPreview(file);
    } catch {
      toast.error("Não foi possível abrir a prévia.");
    } finally {
      setCarregandoPreview(false);
    }
  }

  return (
    <>
      <span className="flex w-full items-center gap-2 rounded-md border border-zinc-200 bg-zinc-50 px-2.5 py-1.5 text-[12px] text-zinc-700 sm:inline-flex sm:w-auto dark:border-border dark:bg-muted/40 dark:text-foreground">
        <Icone className="h-4 w-4 shrink-0 text-zinc-400" />
        <button
          type="button"
          onClick={handleDownload}
          className="min-w-0 flex-1 truncate text-left underline-offset-2 hover:underline sm:max-w-[220px] sm:flex-none"
          title={fileName}
        >
          {fileName}
        </button>
        {extensao && (
          <span className="shrink-0 rounded bg-zinc-200 px-1 py-0.5 text-[10px] font-semibold tracking-wide text-zinc-600 dark:bg-muted dark:text-muted-foreground">
            {extensao}
          </span>
        )}
        <button
          type="button"
          onClick={handlePreview}
          disabled={carregandoPreview}
          className="-my-1.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-zinc-400 transition-colors hover:text-brand-600 disabled:opacity-60 sm:h-6 sm:w-6"
          aria-label="Visualizar arquivo"
          title="Visualizar"
        >
          {carregandoPreview ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
        </button>
        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="-my-1.5 -mr-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-zinc-400 transition-colors hover:text-red-600 sm:h-6 sm:w-6"
            aria-label="Remover anexo"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </span>
      <FilePreviewDialog file={preview} onClose={() => setPreview(null)} />
    </>
  );
}
